// Liability chart — the S3 surface.
//
// Question: "What's it costing us — and who's funding it?"
//
// The math:
//   Annual coin issuance × ₦10 = balance-sheet liability.
//   Today: ~₦18.7M (124K active × ~15 coins/yr) all Bvndle-funded.
//   Target: ~₦600M at scaled earn rate (4 events/user/month × ~10 coins each).
//   Mitigation: consign 60%+ of that liability to partners (IFRS 15 model).
//
// Per row: each category shows annual issuance at target, the consignment
// partner, and Bvndle's exposure after consignment. The split is the lever.
//
// Owner: Kabelo · Ikechukwu (commercial) · Ufuoma (partner negotiations).

import { useState } from "react";

import {
  type ActionRow,
  type ActionStatus,
  addAction,
  deleteAction,
  findRowCommitment,
  updateAction,
} from "../lib/actions";
import {
  CATEGORIES,
  FIT_COLOR,
  FIT_LABEL,
  SIGNUP_BONUS_SOURCE,
  deriveFit,
  type CategoryFit,
} from "../lib/categories";
import { DUE_BY_MAX_OFFSET_DAYS, fmtDueBy, isoInDays, isoToday } from "../lib/due-by";

// Scale assumptions — used to project annual liability per category.
// 124,815 active earners × 480 coins/yr at target ≈ 60M coins × ₦10 = ₦600M.
const TARGET_TOTAL_COIN_ISSUANCE = 60_000_000; // coins / year at target
const COIN_VALUE_NGN = 10;

type RowState = "proposed" | "agreed" | "flight" | "blocked" | "done" | "disagreed";

interface Row {
  code: string;
  label: string;
  earnSharePct: number;
  annualLiabilityNgn: number;
  consignedTargetPct: number;
  bvndleSharePct: number;
  bvndleExposureNgn: number;
  partner: string;
  rationale: string;
  fit: CategoryFit | null;
  commitment?: ActionRow;
}

interface Props {
  kpiId: string;
  actions: ActionRow[];
  currentUser: string;
  onCommitmentChanged: () => Promise<void> | void;
}

function buildRows(kpiId: string, actions: ActionRow[]): Row[] {
  const rows: Row[] = [];

  // signup_bonus is an issuance source, not a category — always 100% Bvndle.
  const signupShare = SIGNUP_BONUS_SOURCE.earnTargetPct;
  const signupLiability = (signupShare / 100) * TARGET_TOTAL_COIN_ISSUANCE * COIN_VALUE_NGN;
  rows.push({
    code: SIGNUP_BONUS_SOURCE.code,
    label: SIGNUP_BONUS_SOURCE.label,
    earnSharePct: signupShare,
    annualLiabilityNgn: signupLiability,
    consignedTargetPct: 0,
    bvndleSharePct: 100,
    bvndleExposureNgn: signupLiability,
    partner: "Bvndle marketing budget",
    rationale: "One-off coin at signup. No counterparty · Bvndle pays. Cap weight to limit exposure.",
    fit: null,
    commitment: findRowCommitment(actions, kpiId, SIGNUP_BONUS_SOURCE.code),
  });

  for (const cat of CATEGORIES) {
    if (!cat.intent || !cat.consignment) continue;
    if (cat.intent.earnTargetPct === 0) continue;
    const share = cat.intent.earnTargetPct;
    const liability = (share / 100) * TARGET_TOTAL_COIN_ISSUANCE * COIN_VALUE_NGN;
    const consigned = cat.consignment.consignedTargetPct;
    const bvndleShare = 100 - consigned;
    const exposure = (bvndleShare / 100) * liability;
    rows.push({
      code: cat.code,
      label: cat.label,
      earnSharePct: share,
      annualLiabilityNgn: liability,
      consignedTargetPct: consigned,
      bvndleSharePct: bvndleShare,
      bvndleExposureNgn: exposure,
      partner: cat.consignment.partner,
      rationale: cat.consignment.rationale,
      fit: deriveFit(cat),
      commitment: findRowCommitment(actions, kpiId, cat.code),
    });
  }

  return rows.sort((a, b) => b.annualLiabilityNgn - a.annualLiabilityNgn);
}

function rowState(c: ActionRow | undefined): RowState {
  if (!c) return "proposed";
  if (c.kind === "disagree") return "disagreed";
  return c.status === "doing" ? "flight" : c.status === "blocked" ? "blocked" : c.status === "watch" ? "agreed" : c.status === "done" ? "done" : "proposed";
}

const STATE_LABEL: Record<RowState, string> = { proposed: "PROPOSED", agreed: "AGREED", flight: "IN FLIGHT", blocked: "BLOCKED", done: "DONE", disagreed: "DISAGREED" };
const STATE_COLOR: Record<RowState, string> = { proposed: "var(--ink-faint)", agreed: "#4fb3ff", flight: "var(--purple)", blocked: "var(--coral)", done: "#5ec98c", disagreed: "var(--ink-faint)" };

function fmtNgn(n: number): string {
  if (n >= 1_000_000_000) return "₦" + (n / 1_000_000_000).toFixed(2) + "B";
  if (n >= 1_000_000) return "₦" + (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return "₦" + (n / 1_000).toFixed(0) + "k";
  return "₦" + n.toFixed(0);
}

export function LiabilityChart({ kpiId, actions, currentUser, onCommitmentChanged }: Props) {
  const rows = buildRows(kpiId, actions);
  const [openCode, setOpenCode] = useState<string | null>(null);

  const totalLiability = rows.reduce((s, r) => s + r.annualLiabilityNgn, 0);
  const totalBvndle = rows.reduce((s, r) => s + r.bvndleExposureNgn, 0);
  const bvndleSharePct = totalLiability > 0 ? (totalBvndle / totalLiability) * 100 : 100;

  return (
    <div style={wrap}>
      <div style={head}>
        <div style={heading}>Annual issuance liability · today vs. target, with consignment</div>
        <div style={subtitle}>
          Every coin issued is ₦10 of liability on Bvndle's balance sheet. Today the number is small because earn is broken (~₦18.7M/yr). Growing earn · to fix the loop · blows up liability unless partners co-fund. The lever is consignment: partners like UBA, MTN, NEPA already carry deferred-revenue loyalty liability; we negotiate them to fund our earn channel from that budget.
        </div>
        <div style={hint}>Click a row to discuss, commit an owner, or disagree.</div>
      </div>

      <div style={heroRow}>
        <HeroTile
          label="Annual coin issuance liability"
          todayValue="₦18.7M"
          todaySub="124K active × ~15 coins/yr · small because earn is broken"
          targetValue={fmtNgn(totalLiability)}
          targetSub="loop scaled to 4 earn events / user / month"
        />
        <HeroTile
          label="Bvndle's share of that liability"
          todayValue="100%"
          todaySub="every coin today is funded by Bvndle"
          targetValue={`${bvndleSharePct.toFixed(0)}%`}
          targetSub={`= ${fmtNgn(totalBvndle)}/yr · partners cover the rest`}
        />
      </div>

      <div style={tableWrap}>
        <div style={tableHead}>
          <span style={{ ...tableHeadCell, flex: 2.2, textAlign: "left" }}>Category</span>
          <span style={{ ...tableHeadCell, flex: 0.8 }}>Earn share</span>
          <span style={{ ...tableHeadCell, flex: 1 }}>Annual ₦</span>
          <span style={{ ...tableHeadCell, flex: 0.8 }}>Consigned</span>
          <span style={{ ...tableHeadCell, flex: 1 }}>Bvndle exposure</span>
          <span style={{ ...tableHeadCell, flex: 1, textAlign: "left" }}>Partner</span>
          <span style={tableHeadCell}>Viability</span>
          <span style={{ ...tableHeadCell, flex: 1.6 }}>Status</span>
          <span style={{ ...tableHeadCell, flex: 1.2, textAlign: "left" }}>Owner</span>
          <span style={{ ...tableHeadCell, flex: 2.2, textAlign: "left" }}>Rationale</span>
          <span style={{ ...tableHeadCell, flex: 0.4 }} />
        </div>
        {rows.map((r) => (
          <RowItem
            key={r.code}
            row={r}
            kpiId={kpiId}
            isOpen={openCode === r.code}
            onToggle={() => setOpenCode((c) => (c === r.code ? null : r.code))}
            currentUser={currentUser}
            onCommitmentChanged={onCommitmentChanged}
          />
        ))}
      </div>
    </div>
  );
}

function HeroTile({ label, todayValue, todaySub, targetValue, targetSub }: { label: string; todayValue: string; todaySub: string; targetValue: string; targetSub: string }) {
  return (
    <div style={heroTile}>
      <div style={heroLabel}>{label}</div>
      <div style={heroBody}>
        <div style={heroSide}>
          <div style={heroSideTag}>TODAY</div>
          <div style={{ ...heroValue, color: "var(--coral)" }}>{todayValue}</div>
          <div style={heroSub}>{todaySub}</div>
        </div>
        <div style={heroArrow}>→</div>
        <div style={heroSide}>
          <div style={heroSideTag}>TARGET</div>
          <div style={{ ...heroValue, color: "#5ec98c" }}>{targetValue}</div>
          <div style={heroSub}>{targetSub}</div>
        </div>
      </div>
    </div>
  );
}

function RowItem({ row, kpiId, isOpen, onToggle, currentUser, onCommitmentChanged }: { row: Row; kpiId: string; isOpen: boolean; onToggle: () => void; currentUser: string; onCommitmentChanged: () => Promise<void> | void }) {
  const state = rowState(row.commitment);
  const disagreed = state === "disagreed";
  const [hover, setHover] = useState(false);
  const hasBlocker = !!row.commitment?.blockers?.trim();
  const exposureColor =
    row.consignedTargetPct >= 70 ? "#5ec98c" : row.consignedTargetPct >= 40 ? "var(--amber)" : "var(--coral)";

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <button
        type="button"
        onClick={onToggle}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        title={state === "proposed" ? "Click to discuss / commit / disagree" : "Click to edit commitment"}
        style={{ ...tableRow, textAlign: "left", background: isOpen ? "var(--bg-1, rgba(255,255,255,0.06))" : hover ? "rgba(83, 74, 183, 0.08)" : "transparent", opacity: disagreed ? 0.55 : 1, cursor: "pointer", width: "100%", border: "none", borderLeft: `3px solid ${isOpen ? "var(--purple)" : "transparent"}`, color: "inherit", transition: "background 120ms ease" }}
      >
        <span style={{ ...tableCell, flex: 2.2, display: "flex", alignItems: "center", gap: 8, textAlign: "left" }}>
          <span style={{ fontWeight: 600, color: "var(--ink)", textDecoration: disagreed ? "line-through" : "none" }}>
            {row.label}
          </span>
        </span>
        <span style={{ ...tableCell, flex: 0.8 }}>{row.earnSharePct.toFixed(0)}%</span>
        <span style={{ ...tableCell, flex: 1, fontWeight: 700 }}>{fmtNgn(row.annualLiabilityNgn)}</span>
        <span style={{ ...tableCell, flex: 0.8, color: exposureColor, fontWeight: 700 }}>{row.consignedTargetPct}%</span>
        <span style={{ ...tableCell, flex: 1, color: row.bvndleSharePct === 100 ? "var(--coral)" : "var(--ink)", fontWeight: 700 }}>
          {fmtNgn(row.bvndleExposureNgn)}
        </span>
        <span style={{ ...tableCell, flex: 1, textAlign: "left", color: "var(--ink-dim)", fontSize: 11 }}>{row.partner}</span>
        <span style={tableCell}>
          {row.fit && <span style={fitChip(row.fit)}>{FIT_LABEL[row.fit]}</span>}
        </span>
        <span style={{ ...tableCell, flex: 1.6, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3 }}>
          <span style={stateChip(state)}>{STATE_LABEL[state]}</span>
          {row.commitment?.dueBy && (
            <span style={dueChip(row.commitment.dueBy)} title={`Due ${fmtDueBy(row.commitment.dueBy)}`}>⏱ due {fmtDueBy(row.commitment.dueBy)}</span>
          )}
          {hasBlocker && <span style={blockerChip} title={row.commitment!.blockers}>✕ {row.commitment!.blockers}</span>}
        </span>
        <span style={{ ...tableCell, flex: 1.2, textAlign: "left", color: row.commitment?.owner ? "var(--ink)" : "var(--ink-faint)", fontWeight: row.commitment?.owner ? 600 : 400 }}>
          {row.commitment?.owner || "—"}
        </span>
        <span style={{ ...tableCell, flex: 2.2, textAlign: "left", color: "var(--ink-dim)", fontSize: 11.5, lineHeight: 1.5 }}>{row.rationale}</span>
        <span style={{ ...tableCell, flex: 0.4, textAlign: "center" }}>
          <span style={chevron(isOpen, state === "proposed" && hover)}>{isOpen ? "▾" : "▸"}</span>
        </span>
      </button>

      {isOpen && (
        <RowEditor row={row} kpiId={kpiId} currentUser={currentUser} onClose={onToggle} onCommitmentChanged={onCommitmentChanged} />
      )}
    </div>
  );
}

function RowEditor({ row, kpiId, currentUser, onClose, onCommitmentChanged }: { row: Row; kpiId: string; currentUser: string; onClose: () => void; onCommitmentChanged: () => Promise<void> | void }) {
  const existing = row.commitment;
  const [owner, setOwner] = useState(existing?.owner ?? "");
  const [doing, setDoing] = useState(existing?.doing ?? "");
  const [blockers, setBlockers] = useState(existing?.blockers ?? "");
  const [dueBy, setDueBy] = useState(existing?.dueBy ?? "");
  const [status, setStatus] = useState<ActionStatus>(existing?.status ?? "watch");
  const [saving, setSaving] = useState(false);
  const focusPath = `${kpiId}:${row.code}`;
  const minDate = isoToday();
  const maxDate = isoInDays(DUE_BY_MAX_OFFSET_DAYS);
  const title = `${row.label} · consign ${row.consignedTargetPct}% to ${row.partner}`;

  async function save(kind: "commitment" | "disagree") {
    setSaving(true);
    try {
      if (existing) await updateAction(existing.id, { kind, owner, doing, blockers,
          dueBy, status: kind === "disagree" ? "watch" : status, title: kind === "disagree" ? `Disagree · ${row.label}` : title });
      else await addAction({ title: kind === "disagree" ? `Disagree · ${row.label}` : title, impacts: [focusPath], owner, status: kind === "disagree" ? "watch" : status, blockers, doing, dueBy, author: currentUser, kind });
      await onCommitmentChanged();
      onClose();
    } finally {
      setSaving(false);
    }
  }
  async function clearCommitment() {
    if (!existing) { onClose(); return; }
    setSaving(true);
    try { await deleteAction(existing.id); await onCommitmentChanged(); onClose(); } finally { setSaving(false); }
  }

  return (
    <div style={editorWrap}>
      <div style={editorHead}>
        <span style={editorEyebrow}>
          {existing ? "EDIT" : "PROPOSE"} · {row.label} · consign {row.consignedTargetPct}% to {row.partner}
        </span>
        <span style={editorRationale}>{row.rationale}</span>
      </div>
      <div style={editorGrid}>
        <label style={editorField}><span style={editorLabel}>Status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value as ActionStatus)} style={editorInput}>
            <option value="watch">Agreed · not yet started</option>
            <option value="doing">In flight</option>
            <option value="blocked">Blocked</option>
            <option value="done">Done · consigned</option>
          </select>
        </label>
        <label style={editorField}><span style={editorLabel}>Owner</span>
          <input value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="Who is committed?" style={editorInput} />
        </label>
        <label style={editorField}><span style={editorLabel}>By when (max 30 days)</span>
          <input type="date" value={dueBy} min={minDate} max={maxDate} onChange={(e) => setDueBy(e.target.value)} style={editorInput} />
        </label>
        <label style={{ ...editorField, gridColumn: "span 2" }}><span style={editorLabel}>Decision / what we're doing</span>
          <input value={doing} onChange={(e) => setDoing(e.target.value)} placeholder={`e.g. 'Open consignment talks with ${row.partner} · model the IFRS 15 offset'`} style={editorInput} />
        </label>
        <label style={{ ...editorField, gridColumn: "span 2" }}><span style={editorLabel}>Blocker (if any)</span>
          <input value={blockers} onChange={(e) => setBlockers(e.target.value)} placeholder="What's in the way?" style={editorInput} />
        </label>
      </div>
      <div style={editorActions}>
        <button type="button" onClick={() => save("commitment")} disabled={saving || !owner.trim()} style={btnPrimary}>
          {existing && existing.kind !== "disagree" ? "Update commitment" : "Commit"}
        </button>
        <button type="button" onClick={() => save("disagree")} disabled={saving} style={btnGhost}>
          {existing?.kind === "disagree" ? "Update disagree" : "Disagree"}
        </button>
        {existing && <button type="button" onClick={clearCommitment} disabled={saving} style={btnDanger}>Clear</button>}
        <span style={{ flex: 1 }} />
        <button type="button" onClick={onClose} disabled={saving} style={btnGhost}>Close</button>
      </div>
    </div>
  );
}

// styles
const wrap: React.CSSProperties = { padding: "16px 18px", borderTop: "1px solid var(--edge)", borderBottom: "1px solid var(--edge)", background: "var(--panel-1, rgba(255,255,255,0.02))", display: "flex", flexDirection: "column", gap: 12 };
const head: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 4 };
const heading: React.CSSProperties = { fontSize: 13, fontWeight: 700, color: "var(--ink)" };
const subtitle: React.CSSProperties = { fontSize: 11.5, color: "var(--ink-faint)", lineHeight: 1.5 };
const hint: React.CSSProperties = { fontFamily: "var(--font-mono)", fontSize: 9.5, letterSpacing: "0.1em", color: "var(--purple)", fontWeight: 700, textTransform: "uppercase", marginTop: 4 };
const heroRow: React.CSSProperties = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 8 };
const heroTile: React.CSSProperties = { border: "1px solid var(--edge)", borderRadius: 4, padding: "12px 14px", background: "var(--bg-1, rgba(255,255,255,0.03))" };
const heroLabel: React.CSSProperties = { fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.1em", color: "var(--ink-faint)", fontWeight: 700, textTransform: "uppercase", marginBottom: 10 };
const heroBody: React.CSSProperties = { display: "flex", alignItems: "center", gap: 16 };
const heroSide: React.CSSProperties = { flex: 1, display: "flex", flexDirection: "column", gap: 3 };
const heroSideTag: React.CSSProperties = { fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "0.12em", color: "var(--ink-faint)", fontWeight: 700 };
const heroValue: React.CSSProperties = { fontSize: 28, fontWeight: 800, lineHeight: 1, fontVariantNumeric: "tabular-nums" };
const heroSub: React.CSSProperties = { fontSize: 11, color: "var(--ink-dim)", lineHeight: 1.4, marginTop: 2 };
const heroArrow: React.CSSProperties = { fontSize: 22, color: "var(--ink-faint)", flexShrink: 0 };
const tableWrap: React.CSSProperties = { marginTop: 8, display: "flex", flexDirection: "column", border: "1px solid var(--edge)", borderRadius: 4 };
const tableHead: React.CSSProperties = { display: "flex", gap: 8, padding: "8px 10px", borderBottom: "1px solid var(--edge)", background: "var(--bg-1, rgba(255,255,255,0.03))" };
const tableHeadCell: React.CSSProperties = { flex: 1, fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "0.08em", color: "var(--ink-faint)", fontWeight: 700, textTransform: "uppercase", textAlign: "right" };
const tableRow: React.CSSProperties = { display: "flex", gap: 8, padding: "9px 10px", borderBottom: "1px solid var(--edge)", alignItems: "center" };
const tableCell: React.CSSProperties = { flex: 1, fontSize: 11.5, color: "var(--ink-dim)", fontVariantNumeric: "tabular-nums", textAlign: "right" };
function fitChip(fit: CategoryFit): React.CSSProperties { const color = FIT_COLOR[fit]; return { display: "inline-block", fontFamily: "var(--font-mono)", fontSize: 8.5, fontWeight: 700, letterSpacing: "0.08em", color, border: `1px solid ${color}`, borderRadius: 3, padding: "1px 5px" }; }
function stateChip(state: RowState): React.CSSProperties { const color = STATE_COLOR[state]; return { display: "inline-block", fontFamily: "var(--font-mono)", fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", color, background: `${color}22`, border: `1px solid ${color}66`, padding: "2px 7px", borderRadius: 3, whiteSpace: "nowrap" }; }
const blockerChip: React.CSSProperties = { display: "inline-block", maxWidth: 220, fontSize: 10, color: "var(--coral)", background: "rgba(196, 74, 74, 0.12)", border: "1px solid rgba(196, 74, 74, 0.4)", padding: "1px 6px", borderRadius: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontStyle: "italic" };
function chevron(isOpen: boolean, emphasised: boolean): React.CSSProperties { return { display: "inline-block", fontSize: 13, color: isOpen ? "var(--purple)" : emphasised ? "var(--purple)" : "var(--ink-faint)", fontWeight: 700, transition: "transform 120ms ease, color 120ms ease" }; }
const editorWrap: React.CSSProperties = { padding: "14px 16px 16px", background: "var(--bg-1, rgba(255,255,255,0.04))", borderBottom: "1px solid var(--edge)", display: "flex", flexDirection: "column", gap: 12 };
const editorHead: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 4 };
const editorEyebrow: React.CSSProperties = { fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.12em", color: "var(--ink-faint)", fontWeight: 700 };
const editorRationale: React.CSSProperties = { fontSize: 11.5, color: "var(--ink-dim)", fontStyle: "italic" };
const editorGrid: React.CSSProperties = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 };
const editorField: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 4 };
const editorLabel: React.CSSProperties = { fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "0.1em", color: "var(--ink-faint)", fontWeight: 700, textTransform: "uppercase" };
const editorInput: React.CSSProperties = { background: "var(--panel-2)", border: "1px solid var(--edge)", borderRadius: 3, padding: "6px 8px", color: "var(--ink)", fontSize: 12, fontFamily: "inherit" };
const editorActions: React.CSSProperties = { display: "flex", gap: 8, alignItems: "center" };
const btnPrimary: React.CSSProperties = { fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.1em", fontWeight: 700, textTransform: "uppercase", padding: "6px 12px", borderRadius: 3, border: "1px solid var(--purple)", background: "var(--purple)", color: "white", cursor: "pointer" };
const btnGhost: React.CSSProperties = { fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.1em", fontWeight: 700, textTransform: "uppercase", padding: "6px 12px", borderRadius: 3, border: "1px solid var(--edge)", background: "transparent", color: "var(--ink-dim)", cursor: "pointer" };
const btnDanger: React.CSSProperties = { fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.1em", fontWeight: 700, textTransform: "uppercase", padding: "6px 12px", borderRadius: 3, border: "1px solid var(--coral)", background: "transparent", color: "var(--coral)", cursor: "pointer" };

function dueChip(iso: string): React.CSSProperties {
  const tone = (() => {
    const d = new Date(iso + "T00:00:00").getTime();
    const today = new Date(new Date().toISOString().slice(0, 10) + "T00:00:00").getTime();
    const days = Math.round((d - today) / 86_400_000);
    if (days < 0) return "var(--coral)";
    if (days <= 7) return "var(--amber)";
    return "#4fb3ff";
  })();
  return {
    display: "inline-block", fontFamily: "var(--font-mono)", fontSize: 9,
    fontWeight: 700, letterSpacing: "0.08em", color: tone,
    background: `${tone}22`, border: `1px solid ${tone}66`,
    padding: "1px 6px", borderRadius: 3, whiteSpace: "nowrap",
  };
}

